/**
 * Risky Shipments Table
 *
 * Displays high-risk containers sorted by monetary value at risk.
 * Shows risk level, value, issues count, and container status.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { AlertTriangle, ChevronLeft, ChevronRight } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { useI18n } from '@/hooks/use-i18n';
import { useRiskyShipments, type RiskyShipment } from '@/hooks/api/use-risky-shipments';
import type { ContainerStatus, RiskLevel } from '@/types/container';
import type { ShipmentStatusFilter } from '@/types/api/containers-recap';

const PAGE_SIZE = 10;

interface RiskyShipmentsTableProps {
    /** Start of date range (ISO format: "2024-01-01") */
    startDate?: string | null;
    /** End of date range (ISO format: "2024-01-31") */
    endDate?: string | null;
    carrierScac?: string;
    shipmentStatus?: ShipmentStatusFilter | '';
    /** Include medium risk containers (>= 0.25) in addition to high/critical */
    includeMedium?: boolean;
    /** Optional title override */
    title?: string;
    /** Optional description override (intro above table) */
    description?: string;
}

// Helper function for risk level badge color
const getRiskLevelColor = (riskLevel: string): 'error' | 'warning' | 'success' | 'gray' => {
    switch (riskLevel.toLowerCase()) {
        case 'critical':
            return 'error';
        case 'high':
            return 'error';
        case 'medium':
            return 'warning';
        case 'low':
            return 'success';
        default:
            return 'gray';
    }
};

// Format currency value
const formatCurrency = (value: number | null, currency: string = 'USD'): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

// Format date
const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

/** Map API shipment status (e.g. BOOKED, in_transit) to ContainerStatus for i18n */
function apiShipmentStatusToContainerStatus(raw: string): ContainerStatus {
    const key = raw.toUpperCase().replace(/ /g, '_');
    const map: Record<string, ContainerStatus> = {
        DRAFT: 'Draft',
        BOOKED: 'Booked',
        IN_TRANSIT: 'In Transit',
        ARRIVED: 'Arrived',
        DELIVERED: 'Delivered',
        ON_HOLD: 'On Hold',
        CANCELLED: 'Cancelled',
        BOOKING_CONFIRMED: 'Booking Confirmed',
        DOCUMENTS_PENDING: 'Documents Pending',
        READY_TO_SHIP: 'Ready to Ship',
        ARRIVED_AT_PORT: 'At Port',
        CUSTOMS_CLEARANCE: 'Customs Clearance',
        CUSTOMS_CLEARED: 'Customs Cleared',
        OUT_FOR_DELIVERY: 'Out for Delivery',
        COMPLETED: 'Completed',
    };
    return map[key] ?? 'Draft';
}

export function RiskyShipmentsTable({
    startDate = null,
    endDate = null,
    carrierScac,
    shipmentStatus,
    includeMedium = false,
    title,
    description,
}: RiskyShipmentsTableProps) {
    const { t } = useTranslation(['pages:dashboard', 'pages:containers', 'common']);
    const { translateRiskLevel, translateContainerStatus } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);

    useEffect(() => {
        setPage(0);
    }, [startDate, endDate, carrierScac, shipmentStatus, includeMedium]);

    const skip = page * PAGE_SIZE;

    const { data, isLoading, error } = useRiskyShipments({
        startDate,
        endDate,
        skip,
        limit: PAGE_SIZE,
        includeMedium,
        enabled: true,
        carrierScac,
        shipmentStatus: shipmentStatus || undefined,
    });

    const displayTitle = title || t('controlHub.riskyShipments.title', { defaultValue: 'Risky Shipments' });
    const displayDescription =
        description ?? t('controlHub.riskyShipments.description', { defaultValue: 'High-risk containers sorted by value at risk' });
    const containers = data?.containers ?? [];
    const totalCount = data?.total_count ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const viewAllHref = useMemo(() => {
        const sp = new URLSearchParams();
        sp.set('risk', 'medium,high,critical');
        if (startDate) sp.set('start_date', startDate);
        if (endDate) sp.set('end_date', endDate);
        if (carrierScac) sp.set('carrier', carrierScac);
        if (shipmentStatus) sp.set('status', shipmentStatus);
        return `/shipper/containers?${sp.toString()}`;
    }, [startDate, endDate, carrierScac, shipmentStatus]);

    // Empty state
    if (!isLoading && containers.length === 0) {
        return (
            <TableCard.Root>
                <TableCard.Header title={displayTitle} description={displayDescription} />
                <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="rounded-full bg-success-50 p-3 mb-4">
                        <AlertTriangle className="size-6 text-success-600" />
                    </div>
                    <p className="text-sm text-tertiary text-center">
                        {t('controlHub.riskyShipments.emptyState', { defaultValue: 'No high-risk shipments found' })}
                    </p>
                    <p className="text-xs text-quaternary text-center mt-1">
                        All shipments are within acceptable risk levels
                    </p>
                </div>
            </TableCard.Root>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <TableCard.Root>
                <TableCard.Header title={displayTitle} description={displayDescription} />
                <div className="flex items-center justify-center py-12">
                    <div className="animate-pulse flex items-center gap-2">
                        <div className="h-4 w-4 bg-tertiary/30 rounded-full" />
                        <div className="h-4 w-32 bg-tertiary/30 rounded" />
                    </div>
                </div>
            </TableCard.Root>
        );
    }

    // Error state
    if (error) {
        return (
            <TableCard.Root>
                <TableCard.Header title={displayTitle} description={displayDescription} />
                <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-sm text-error-600">Failed to load risky shipments</p>
                </div>
            </TableCard.Root>
        );
    }

    return (
        <TableCard.Root>
            <TableCard.Header
                title={displayTitle}
                description={displayDescription}
                badge={totalCount}
                contentTrailing={
                    totalCount > PAGE_SIZE ? (
                        <Link
                            to={viewAllHref}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            {t('controlHub.riskyShipments.viewAll', { defaultValue: 'View all' })} ({totalCount})
                            <ChevronRight className="size-4" />
                        </Link>
                    ) : undefined
                }
            />
            <Table>
                <Table.Header>
                    <Table.Head label="Container Ref." isRowHeader />
                    <Table.Head label="Route" />
                    <Table.Head label="Risk" />
                    <Table.Head label="Value at Risk" />
                    <Table.Head label="Issues" />
                    <Table.Head label={t('controlHub.riskyShipments.colStatus', { defaultValue: 'Status' })} />
                    <Table.Head label="Alerts" />
                    <Table.Head label="ETA" />
                </Table.Header>
                <Table.Body>
                    {containers.map((shipment: RiskyShipment) => (
                        <Table.Row
                            key={shipment.container_id}
                            className="cursor-pointer hover:bg-secondary"
                            onClick={() =>
                                navigate(`/shipper/containers/${shipment.container_id}`, {
                                    state: navigationStateFrom(location),
                                })
                            }
                        >
                            <Table.Cell>
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="size-4 text-error-500" />
                                    <span className="font-medium text-primary">{shipment.container_number}</span>
                                </div>
                            </Table.Cell>

                            <Table.Cell>
                                <div className="text-sm text-tertiary">
                                    <span>{shipment.origin_country_code || '-'}</span>
                                    <span className="mx-1">→</span>
                                    <span>{shipment.destination_country_code || '-'}</span>
                                </div>
                                {(shipment.origin_port_name || shipment.destination_port_name) && (
                                    <div className="text-xs text-quaternary mt-0.5">
                                        {shipment.origin_port_name || '-'} → {shipment.destination_port_name || '-'}
                                    </div>
                                )}
                            </Table.Cell>

                            <Table.Cell>
                                <div className="flex flex-col gap-1">
                                    <Badge color={getRiskLevelColor(shipment.risk_level)} size="sm">
                                        {translateRiskLevel(
                                            (shipment.risk_level.charAt(0).toUpperCase() +
                                                shipment.risk_level.slice(1).toLowerCase()) as RiskLevel,
                                        )}
                                    </Badge>
                                    <span className="text-xs text-quaternary">Score: {(shipment.risk_score * 100).toFixed(0)}%</span>
                                </div>
                            </Table.Cell>

                            <Table.Cell>
                                <span className="font-medium text-primary">{formatCurrency(shipment.total_value_usd)}</span>
                                {shipment.freight_charges_usd && (
                                    <div className="text-xs text-quaternary">Freight: {formatCurrency(shipment.freight_charges_usd)}</div>
                                )}
                            </Table.Cell>

                            <Table.Cell>
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`font-medium ${
                                            shipment.open_issues_count > 0 ? 'text-warning-600' : 'text-success-600'
                                        }`}
                                    >
                                        {shipment.open_issues_count}
                                    </span>
                                    {shipment.blocking_issues_count > 0 && (
                                        <Badge color="error" size="sm">
                                            {shipment.blocking_issues_count} blocking
                                        </Badge>
                                    )}
                                </div>
                            </Table.Cell>

                            <Table.Cell>
                                <span className="text-sm text-primary">
                                    {translateContainerStatus(apiShipmentStatusToContainerStatus(shipment.status))}
                                </span>
                            </Table.Cell>

                            <Table.Cell>
                                {shipment.has_active_alerts ? (
                                    <Badge color="error" size="sm">
                                        {shipment.alert_count} active
                                    </Badge>
                                ) : (
                                    <span className="text-sm text-tertiary">None</span>
                                )}
                            </Table.Cell>

                            <Table.Cell>
                                <span className="text-sm text-tertiary">{formatDate(shipment.estimated_arrival_date)}</span>
                                {shipment.carrier_name && (
                                    <div className="text-xs text-quaternary">{shipment.carrier_name}</div>
                                )}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-secondary px-4 py-3 md:px-6">
                    <span className="text-xs text-tertiary">
                        {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            className="p-1 rounded-md text-tertiary hover:text-primary hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            aria-label="Previous page"
                        >
                            <ChevronLeft className="size-4" />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setPage(i)}
                                className={`size-7 rounded-md text-xs font-medium transition-colors ${
                                    page === i ? 'bg-brand-50 text-brand-700' : 'text-tertiary hover:text-primary hover:bg-secondary'
                                }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                            className="p-1 rounded-md text-tertiary hover:text-primary hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            aria-label="Next page"
                        >
                            <ChevronRight className="size-4" />
                        </button>
                    </div>
                </div>
            )}
        </TableCard.Root>
    );
}
